import { ColDef, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { TimeFormat } from '@benzinga/date-utils';
import { DateTime } from 'luxon';
import { NO_VALUE } from '../../../Ticker';
import { formatDisplayValueOrDefault } from '../../helpers/getCellValue';
import {
  COMPACT_DATE_FORMAT,
  DISPLAY_DATE_FORMAT,
  DISPLAY_DATE_FORMATS,
  LOCALIZED_DATE_FORMATS,
  MARKET_DATA_SOURCE_ZONE,
} from './formats';
import { compareDateFilterCell, DateFilterCellValue } from './dateFilter';

export const isLocalizedDateValue = (value: unknown): boolean => {
  if (value == null || value === '') return false;

  const str = String(value);
  return LOCALIZED_DATE_FORMATS.some(format => DateTime.fromFormat(str, format).isValid);
};

/** Parses date-only cell values (earnings date, dividend ex date, etc.). */
export const parseCellDate = (value: unknown, timezone?: string): DateTime | null => {
  if (value == null || value === '') return null;

  const str = String(value);
  const displayZone = timezone ?? MARKET_DATA_SOURCE_ZONE;

  // Compact dates name a calendar day rather than an instant, so they are read
  // straight into the display zone. Converting Eastern midnight instead would
  // roll the day back for users west of Eastern.
  if (/^\d{8}$/.test(str)) {
    const dt = DateTime.fromFormat(str, COMPACT_DATE_FORMAT, { zone: displayZone });
    return dt.isValid ? dt : null;
  }

  const fromIso = DateTime.fromISO(str, { zone: displayZone });
  if (fromIso.isValid) return fromIso;

  for (const format of LOCALIZED_DATE_FORMATS) {
    const dt = DateTime.fromFormat(str, format, { zone: displayZone });
    if (dt.isValid) return dt;
  }

  return null;
};

export const parseDisplayDate = (value: unknown): DateTime | null => {
  if (value == null || value === '') return null;

  const str = String(value);
  for (const format of DISPLAY_DATE_FORMATS) {
    const dt = DateTime.fromFormat(str, format, { zone: 'utc' });
    if (dt.isValid) return dt;
  }

  const fromIso = DateTime.fromISO(str, { zone: 'utc' });
  return fromIso.isValid ? fromIso : null;
};

/** Sorts unparseable cells (NO_VALUE, blanks) first, matching ag-grid's null handling. */
export const compareDisplayDate = (valueA: string, valueB: string): number => {
  const dateA = parseDisplayDate(valueA);
  const dateB = parseDisplayDate(valueB);

  if (!dateA && !dateB) return 0;
  if (!dateA) return -1;
  if (!dateB) return 1;

  return dateA.toMillis() - dateB.toMillis();
};

export interface DateColDefTimeSettings {
  timeFormat: TimeFormat;
  timeOffset: number;
  timezone?: string;
}

/** Date-only column. For combined date+time fields use DateTimeColDef(colDef, settings). */
export const DateColDef = (colDef: ColDef, { timezone }: DateColDefTimeSettings): ColDef => ({
  cellStyle: { textAlign: 'left' },
  comparator: compareDisplayDate,
  filter: 'agDateColumnFilter',
  filterParams: {
    comparator: (filterLocalDateAtMidnight: Date, cellValue: DateFilterCellValue) =>
      compareDateFilterCell(filterLocalDateAtMidnight, cellValue, parseDisplayDate, timezone),
  },
  filterValueGetter: ({ colDef: { field = 'time' }, data }: ValueGetterParams) => {
    const dateTime = parseCellDate(data[field], timezone);
    return dateTime?.isValid ? dateTime.toJSDate() : null;
  },
  valueFormatter: ({ value }: ValueFormatterParams): string => formatDisplayValueOrDefault(value),
  valueGetter: ({ colDef: { field = 'time' }, data }: ValueGetterParams) => {
    const rawValue = data[field];
    if (rawValue == null || rawValue === '') return NO_VALUE;

    if (isLocalizedDateValue(rawValue)) {
      const dateTime = parseCellDate(rawValue, timezone);
      return dateTime?.isValid ? dateTime.toFormat(DISPLAY_DATE_FORMAT) : String(rawValue);
    }

    const dateTime = parseCellDate(rawValue, timezone);
    if (!dateTime?.isValid) return NO_VALUE;

    return dateTime.toFormat(DISPLAY_DATE_FORMAT);
  },
  ...colDef,
});
